import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import LightGenerator from "./LightGenerator";
import APP_CAMERA_POSITIONS from "@/constants/APP_CAMERA_POSITIONS";

// interface
// appRenderer.start();
// appRenderer.stop();

let lastActiveAnimationUuid = null;

export default class AppRenderer {
  /**
   * @param {AppModel} appModel
   * @param {*} param1
   * @returns
   */
  static create(appModel, { width = 400, height = 400 } = {} /* size */, cameraPosition, animationSpeed = 0.001) {
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(window.devicePixelRatio); // https://developer.mozilla.org/en-US/docs/Web/API/Window/devicePixelRatio

    // scene
    const scene = new THREE.Scene();

    // camera
    const camera = new THREE.PerspectiveCamera(
      40, // fov
      1, // aspect
      0.1, // near
      2000, // far
    );

    if (!cameraPosition) console.warn("[AppRenderer]cameraPosition is not provided. Using default camera position.");
    const { x, y, z, _x, _y, _z } = cameraPosition || APP_CAMERA_POSITIONS.DEFAULT;
    camera.position.set(x, y, z);
    // camera.lookAt(x, y, z);
    // camera.setViewOffset(width, height, 0, 0, width, height);

    // light
    scene.add(LightGenerator.create({ x: 0, y: 0, z: 2 }, 4)); // front
    scene.add(LightGenerator.create({ x: 0, y: 0, z: -2 }, 1)); // back
    scene.add(LightGenerator.create({ x: 2, y: 0, z: 0 }, 2)); // left
    scene.add(LightGenerator.create({ x: -2, y: 0, z: 0 }, 2)); // right

    const model = appModel.scene;
    scene.add(model);

    const gltfAnimations = appModel.animations;

    const mixer = new THREE.AnimationMixer(model);

    const whenFinished = [];
    mixer.addEventListener("finished", (e) => {
      // e e.action._clip.name e.action.isRunning() e.action._clip.uuid

      if (lastActiveAnimationUuid === e.action._clip.uuid) {
        whenFinished.forEach((fn) => fn(e));
        // appRenderer.appAnimationController.play("{{loop}}idle-40");
        // mixerMap["{{loop}}idle-40"].play();
      }

      // whenFinished.forEach((fn) => fn(e));
    });
    // const mixer = new THREE.AnimationClip(model);
    this._mixer = mixer;

    const mixerMap = {};
    gltfAnimations.forEach((aniclip) => {
      const { name } = aniclip;
      const action /* AnimationAction */ = mixer.clipAction(aniclip);
      action.clampWhenFinished = true;
      // action.setLoop(THREE.LoopPingPong, 1); // MARKER : loop
      const loop = name.toLowerCase().includes("{{loop}}") ? THREE.LoopRepeat : THREE.LoopOnce;
      action.setLoop(loop);
      mixerMap[name.toLowerCase()] = action;
    });

    // controllers
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(_x, _y, _z);
    controls.update();

    const appRenderer = new AppRenderer(
      appModel,
      renderer.domElement,
      renderer,
      scene,
      camera,
      mixer,
      mixerMap,
      controls,
      animationSpeed,
      whenFinished,
    );

    // render function
    const render = (time) => {
      appRenderer.render(time);
      window.requestAnimationFrame(render);

      if (window.__bool) {
        window.__imgData = renderer.domElement.toDataURL();
        window.__bool = false;
      }
    };

    // Capture Download를 위해서
    window.render = render;

    const createdFunction = window.requestAnimationFrame(render);
    // cancelAnimationFrame(createdFunction); // destroy all

    // init
    camera.updateProjectionMatrix();
    renderer.setSize(width, height); // window resize에 달아야 함 // 이게 wrapper의 element랑 동일해야함. 안그러면 overflow

    return appRenderer;
  }

  constructor(
    appModel,
    canvasEl,
    renderer,
    scene,
    camera,
    mixer,
    mixerMap = {},
    controls,
    animationSpeed,
    whenFinished = [],
  ) {
    this.appModel = appModel;
    this.canvasEl = canvasEl;
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;
    this.mixer = mixer;
    this.mixerMap = mixerMap;
    this.controls = controls;

    this.appAnimationController = new AppAnimationController(this.mixer, this.mixerMap);

    this.initCameraPosition = camera.position.clone();
    this.initCameraRotation = camera.rotation.clone();

    this.animationSpeed = animationSpeed;

    this.whenFinished = whenFinished;
  }

  restoreCamera() {
    this.camera.position.copy(this.initCameraPosition);
    this.camera.rotation.copy(this.initCameraRotation);
    this.camera.updateProjectionMatrix();
  }

  // 렌더러/카메라를 새 크기로 (리로드 없이 선명하게 재렌더). 상주 모델 페이지별 크기 전환용.
  resize(width, height) {
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  stop() {}

  clock = new THREE.Clock();

  // 아직 왜 있는지 모르겠는 것들
  render(time) {
    this.renderer.render(this.scene, this.camera);

    time *= this.animationSpeed || 0.001; // second unit

    if (this.mixer) {
      // 첫 프레임엔 _previousTime이 없어 delta가 NaN → mixer.update(NaN)이 재생중 액션 time을
      // 영구 오염(정지)시킨다. 첫 프레임은 delta=0으로 가드.
      const deltaTime = this._previousTime == null ? 0 : time - this._previousTime;
      // const deltaTime = this.clock.getDelta();
      this.mixer.update(deltaTime);
    }

    this._previousTime = time;
  }

  createOnResize(wrapperEl) {
    // window가 resize될 때마다 호출되는 함수를 반환
    // window resize에 달아야 함
    // console.log("resize");
    return () => {
      const width = wrapperEl.clientWidth;
      const height = wrapperEl.clientHeight;
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(width, height);
    };
  }

  play(key) {
    Object.keys(this.mixerMap).forEach((key) => {
      this.mixerMap[key].stop();
    });

    this.mixerMap[key].play();
  }

  // 현재 모델을 animationId의 t01(0~1) 지점 포즈로 고정 후 1프레임 렌더 → PNG dataURL 반환 (인생네컷 캡처용).
  // alpha:true 렌더러라 배경 투명. preserveDrawingBuffer 미설정이지만 render 직후 동기 toDataURL이라 동작.
  poseAndCapture(animationId, t01) {
    this.appAnimationController.pose(animationId, t01);
    this.renderer.render(this.scene, this.camera);
    return this.renderer.domElement.toDataURL("image/png");
  }

  addFinishedListener(fn) {
    this.whenFinished.push(fn);
  }

  _debugCameraPosition() {
    // console.log(this.camera.position);
    // console.log(this.controls.target); // orbit controls target
    return {
      ...this.camera.position,
      _x: this.controls.target.x,
      _y: this.controls.target.y,
      _z: this.controls.target.z,
    };
  }
}

class AppAnimationController {
  currentAction = null;

  constructor(mixer, mixerMap) {
    this._mixer = mixer;
    this._animationMap = mixerMap;
    this._meta = Object.keys(mixerMap)
      .filter((key) => !key.startsWith("_"))
      .map((key) => {
        return { animationId: key.toLowerCase() };
      });

    this.mixerMap = mixerMap;
  }

  get meta() {
    return this._meta;
  }

  /**
   * @param {string} key  // animationId 'happy'
   */
  // 확실히 차이가 있네 angry -> waving 갈때 보면 순간적으로 T-pose로 됨.
  // 모델이 간혹 재생이 안되는게 있어서,  deprecarted
  // WARN: 이게 동작하려면 ModelFacade에서 this._appRenderer.appAnimationController.stop()을 삭제해야 한다
  play(animationId, { loop = false } = {}) {
    // 이렇게 하면 안되지만 일단 편의를 위해서 이렇게 하자 나중에 리팩터해야됨
    animationId = normalize(animationId);
    const activeAction = this._animationMap[animationId];

    if (!activeAction) return console.warn("W_NOT_FOUND_ANIMATION : ", animationId);

    // 동적 루프: 클립 duration보다 길게 배치된 모션은 반복 재생(끝은 호출측 타이머가 idle 복귀로 컷).
    //  기본은 초기 설정 유지({{loop}} 이름=Repeat, 그 외 Once). LoopRepeat면 mixer 'finished'가 안 떠 idle 리스너 미발동.
    const clip = activeAction.getClip ? activeAction.getClip() : activeAction._clip;
    const isLoopNamed = !!(clip && clip.name && clip.name.toLowerCase().includes("{{loop}}"));
    activeAction.setLoop(loop || isLoopNamed ? THREE.LoopRepeat : THREE.LoopOnce, Infinity);

    const previousAction = this.currentAction;
    if (previousAction && previousAction !== activeAction) {
      // warp=false 필수: warp=true는 페이드 동안 timeScale을 클립 길이비로 왜곡하고, 워프가 끝나면
      //  "나가는 액션"의 timeScale에 그 비율(idleDur/motionDur 등)이 영구 저장된다. 액션은 mixerMap에서
      //  재사용되고 reset()은 timeScale을 복원하지 않으므로 → 다음 idle 복귀가 ~8배속으로 재생되는 버그.
      previousAction.crossFadeTo(activeAction, 1, false);
    }

    activeAction.reset(); // stopFading/stopWarping 포함
    activeAction.setEffectiveTimeScale(1); // 과거 warp 오염 방어적 정상화 (www는 강제 speed=1로 우회했던 문제)
    activeAction.setEffectiveWeight(1);
    activeAction.play(); // 고려 crossFadeFrom
    lastActiveAnimationUuid = activeAction._clip.uuid;

    // animationAction.isRunning

    this.currentAction = activeAction;
  }

  // play(animationId) {
  //   // this.stop();

  //   // 이렇게 하면 안되지만 일단 편의를 위해서 이렇게 하자 나중에 리팩터해야됨
  //   animationId = normalize(animationId);

  //   if (this._animationMap[animationId]) {
  //     this._animationMap[animationId].play();
  //   } else {
  //     console.warn("W_NOT_FOUND_ANIMATION : ", animationId);
  //   }
  // }

  stop() {
    // console.log("stopped");
    Object.values(this._animationMap).forEach((action) => action.stop());
  }

  // 현재 액션을 현재 프레임에서 동결/해제 — 편집기 일시정지·스크럽용 (stop()은 바인드포즈=T-pose가 됨)
  pauseCurrent() {
    if (this.currentAction) this.currentAction.paused = true;
  }
  resumeCurrent() {
    if (this.currentAction) this.currentAction.paused = false;
  }

  // 특정 애니메이션의 t01(0~1) 지점 포즈로 고정 (캡처용). paused=true라 RAF 루프의 mixer.update가 진행시키지 않음.
  pose(animationId, t01) {
    animationId = normalize(animationId);
    const action = this._animationMap[animationId];
    if (!action) return console.warn("W_NOT_FOUND_ANIMATION : ", animationId);

    Object.values(this._animationMap).forEach((a) => a.stop());
    action.reset();
    action.play();
    action.paused = true;

    const clip = action.getClip ? action.getClip() : action._clip;
    const duration = (clip && clip.duration) || 1;
    action.time = Math.max(0, Math.min(1, t01)) * duration;

    this._mixer.update(0); // 설정한 time의 포즈를 즉시 반영
    this.currentAction = action;
  }
}

function normalize(animationId) {
  // WARN: v4에만 사용할것
  // if (animationId.startsWith("animation--legacy-")) return animationId.replace("animation--legacy-", "");
  if (animationId.startsWith("animation--")) return animationId.replace("animation--", "");
  else return animationId;
}
